import tinycolor from 'tinycolor2';
import { doNothing } from '@/utils/doNothing';
import { getContrastTextColor } from './getContrastTextColor';
import { getReadableTextColor } from './getReadableTextColor';

const ACCOUNT_COLOR_STORAGE_KEY = 'khova:accountColorHsl';

const HSL_TRIPLE_PATTERN = /^\d{1,3} \d{1,3}% \d{1,3}%$/;

const tripleToHslString = (triple: string): string | null => {
  if (!HSL_TRIPLE_PATTERN.test(triple)) {
    return null;
  }
  const [h, s, l] = triple.split(' ');
  return `hsl(${h}, ${s}, ${l})`;
};

const toHslTriple = (color: string): string => {
  const { h, s, l } = tinycolor(color).toHsl();
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

export const applyColorToDocument = (hsl: string): void => {
  const root = document.documentElement;
  root.style.setProperty('--primary', hsl);
  root.style.setProperty('--accent', hsl);
  root.style.setProperty('--ring', hsl);

  const parsed = tripleToHslString(hsl);
  if (!parsed) {
    return;
  }

  const foreground = toHslTriple(getContrastTextColor(parsed));
  root.style.setProperty('--primary-foreground', foreground);
  root.style.setProperty('--accent-foreground', foreground);
  root.style.setProperty('--primary-readable', toHslTriple(getReadableTextColor(parsed)));
};

export const resetColorOnDocument = (): void => {
  const root = document.documentElement;
  root.style.removeProperty('--primary');
  root.style.removeProperty('--accent');
  root.style.removeProperty('--ring');
  root.style.removeProperty('--primary-foreground');
  root.style.removeProperty('--accent-foreground');
  root.style.removeProperty('--primary-readable');
};

export const cacheAccountColor = (hsl: string): void => {
  try {
    localStorage.setItem(ACCOUNT_COLOR_STORAGE_KEY, hsl);
  } catch {
    doNothing();
  }
};

export const clearCachedAccountColor = (): void => {
  try {
    localStorage.removeItem(ACCOUNT_COLOR_STORAGE_KEY);
  } catch {
    doNothing();
  }
};

const readCachedAccountColor = (): string | null => {
  try {
    return localStorage.getItem(ACCOUNT_COLOR_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const hasCachedAccountColor = (): boolean => {
  const hsl = readCachedAccountColor();
  return !!hsl && HSL_TRIPLE_PATTERN.test(hsl);
};

export const applyCachedAccountColor = (): boolean => {
  const hsl = readCachedAccountColor();
  if (!hsl || !HSL_TRIPLE_PATTERN.test(hsl)) {
    clearCachedAccountColor();
    return false;
  }
  applyColorToDocument(hsl);
  return true;
};

export const hexToHsl = (hex: string): string | null => {
  const color = tinycolor(hex);
  return color.isValid() ? toHslTriple(hex) : null;
};
